import {
  YNABApiResponse,
  YNABBudgetsResponse,
  YNABCategoriesResponse,
  YNABTransactionsResponse,
  YNABScheduledTransactionsResponse,
  YNABAuthResponse,
} from '../types/ynab';

class YNABService {
  private baseUrl: string;
  private clientId: string;
  private redirectUri: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_YNAB_API_BASE_URL || 'https://api.youneedabudget.com/v1';
    this.clientId = import.meta.env.VITE_YNAB_CLIENT_ID || '';
    this.redirectUri = import.meta.env.VITE_YNAB_REDIRECT_URI || 'http://localhost:5173/auth/callback';
  }

  /**
   * Generate OAuth authorization URL for YNAB implicit grant flow
   */
  getAuthorizationUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'token',
      scope: 'read-only',
    });

    return `https://app.youneedabudget.com/oauth/authorize?${params.toString()}`;
  }

  /**
   * Parse access token from OAuth callback URL fragment
   */
  parseAuthCallback(hash: string): YNABAuthResponse | null {
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const tokenType = params.get('token_type');
    const expiresIn = params.get('expires_in');
    const scope = params.get('scope');

    if (!accessToken || !tokenType || !expiresIn) {
      return null;
    }

    return {
      access_token: accessToken,
      token_type: tokenType,
      expires_in: parseInt(expiresIn, 10),
      scope: scope || '',
    };
  }

  /**
   * Make authenticated API request to YNAB
   */
  private async makeRequest<T>(endpoint: string, accessToken: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`YNAB API request failed: ${response.status} ${response.statusText}`);
    }

    const data: YNABApiResponse<T> = await response.json();
    return data.data;
  }

  /**
   * Get all budgets for the authenticated user
   */
  async getBudgets(accessToken: string): Promise<YNABBudgetsResponse> {
    return this.makeRequest<YNABBudgetsResponse>('/budgets', accessToken);
  }

  /**
   * Get categories for a specific budget
   */
  async getCategories(budgetId: string, accessToken: string): Promise<YNABCategoriesResponse> {
    return this.makeRequest<YNABCategoriesResponse>(`/budgets/${budgetId}/categories`, accessToken);
  }

  /**
   * Get transactions for a specific budget and date range
   */
  async getTransactions(
    budgetId: string,
    accessToken: string,
    sinceDate?: string
  ): Promise<YNABTransactionsResponse> {
    const endpoint = sinceDate
      ? `/budgets/${budgetId}/transactions?since_date=${sinceDate}`
      : `/budgets/${budgetId}/transactions`;
    const response = await this.makeRequest<YNABTransactionsResponse>(endpoint, accessToken);
    // Convert date strings to Date objects (local time)
    const { parseLocalDate } = await import('../utils/dateUtils');
    const transactions = response.transactions.map(tx => ({
      ...tx,
      date: parseLocalDate(typeof tx.date === 'string' ? tx.date : ''),
      subtransactions: tx.subtransactions?.map(sub => ({
        ...sub,
        // If subtransactions have date fields, parse here as well
      })) || [],
    }));
    return { ...response, transactions };
  }

  /**
   * Get scheduled transactions for a specific budget
   */
  async getScheduledTransactions(
    budgetId: string,
    accessToken: string
  ): Promise<YNABScheduledTransactionsResponse> {
    const response = await this.makeRequest<YNABScheduledTransactionsResponse>(
      `/budgets/${budgetId}/scheduled_transactions`,
      accessToken
    );
    const { parseLocalDate } = await import('../utils/dateUtils');
    const scheduled_transactions = response.scheduled_transactions.map(tx => ({
      ...tx,
      date_first: parseLocalDate(typeof tx.date_first === 'string' ? tx.date_first : ''),
      date_next: parseLocalDate(typeof tx.date_next === 'string' ? tx.date_next : ''),
    }));
    return { ...response, scheduled_transactions };
  }
}

export const ynabService = new YNABService();
